import { useState, useEffect } from "react"
import { motion } from "framer-motion";


const RadarDisplay = ({ logs = [] }) => {
  const [sweepAngle, setSweepAngle] = useState(0)
  const [hoveredSignal, setHoveredSignal] = useState(null)
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 })

  // Convert logs to radar signals
  const convertLogsToSignals = (logs) => {
    return logs.map((log, index) => {
      // Generate angle based on index and timestamp for consistent positioning
      const angle = (index * (360 / logs.length) + Date.parse(log.timestamp) / 1000000) % (2 * Math.PI)

      // Calculate distance based on score (higher score = further from center)
      const distance = 60 + (log.score / 100) * 140 // Range from 60 to 200

      // Determine signal type based on signal_type
      let type = "low"
      if (log.score >= 85) {
        type = "critical"
      } else if (log.score >= 70 && log.score < 85) {
        type = "high"
      } else if (log.score >= 50 && log.score < 70) {
        type = "medium"
      }

      return {
        id: log.id,
        x: 250 + Math.cos(angle) * distance,
        y: 250 + Math.sin(angle) * distance,
        type,
        score: log.score,
        signal_type: log.signal_type.replace(/\*\*/g, ""), // Remove markdown formatting
        raw_signal: log.raw_signal,
        enrichment: log.enrichment,
        timestamp: log.timestamp,
        industry: log.industry,
        decision: log.decision,
        age: 0,
      }
    })
  }

  const [signals, setSignals] = useState([])

  // Update signals when logs change
  useEffect(() => {
    if (logs && logs.length > 0) {
      setSignals(convertLogsToSignals(logs))
    }
  }, [logs])

  // Radar sweep animation
  useEffect(() => {
    const interval = setInterval(() => {
      setSweepAngle((prev) => (prev + 2) % 360)
    }, 50)
    return () => clearInterval(interval)
  }, [])

  const getSignalFillColor = (type) => {
    switch (type) {
      case "critical":
        return "239, 68, 68" // red
      case "high":
        return "249, 115, 22" // orange
      case "medium":
        return "234, 179, 8" // yellow
      default:
        return "34, 197, 94" // green
    }
  }

  const getTagColor = (score) => {
    if (score >= 85) {
      return "text-red-400 bg-red-400/20"
    } else if (score >= 70 && score < 85) {
      return "text-orange-400 bg-orange-400/20"
    } else if (score >= 50 && score < 70) {
      return "text-yellow-400 bg-yellow-400/20"
    } else {
      return "text-green-400 bg-green-400/20"
    }
  }

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect()
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      containerWidth: rect.width,
      containerHeight: rect.height,
    })
  }

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString()
  }

  const truncateText = (text, maxLength = 100) => {
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text
  }

  return (
    <div className="relative w-full h-full p-4 bg-white" 
    onMouseMove={handleMouseMove} 
    ref={(el) => {
        if (el) {
          // Store container bounds for tooltip positioning
          window.radarContainer = el.getBoundingClientRect()
        }
      }}
    >
      {/* Header */}
      <div className="absolute top-[-15px] left-[-40px] z-10">
        <h2 className="text-xs font-bold tracking-wider text-gray-700 opacity-80">TACTICAL RADAR SYSTEM</h2>
        {/* <p className="text-xs text-gray-600 opacity-60">Industry: {logs[0]?.industry || "N/A"}</p> */}
      </div>

      {/* SVG Radar Display */}
      <svg className="w-full h-full" viewBox="0 0 500 500">
        {/* Background circles with score indicators */}
        {[
          { radius: 100, score: "20" },
          { radius: 150, score: "50" },
          { radius: 200, score: "75" },
          { radius: 250, score: "100" },
        ].map(({ radius, score }, i) => (
          <g key={i}>
            <circle
              cx="250"
              cy="250"
              r={radius}
              fill="none"
              stroke="rgba(107, 114, 128, 0.4)"
              strokeWidth="1"
              strokeDasharray="2,2"
            />
            <text x={250 + radius - 10} y="255" className="fill-gray-500 text-xs font-mono opacity-60">
              {score}
            </text>
          </g>
        ))}

        {/* Cross lines */}
        <line x1="50" y1="250" x2="450" y2="250" stroke="rgba(107, 114, 128, 0.4)" strokeWidth="1" />
        <line x1="250" y1="50" x2="250" y2="450" stroke="rgba(107, 114, 128, 0.4)" strokeWidth="1" />

        {/* Sweep line */}
        <line
          x1="250"
          y1="250"
          x2={250 + Math.cos(((sweepAngle - 90) * Math.PI) / 180) * 200}
          y2={250 + Math.sin(((sweepAngle - 90) * Math.PI) / 180) * 200}
          stroke="rgba(34, 197, 94, 0.8)"
          strokeWidth="2"
          className="drop-shadow-lg"
        />

        {/* Signal points */}
        {signals.map((signal) => {
          const fillColor = getSignalFillColor(signal.type)
          const opacity = 0.8
          const isHighPriority = signal.type === "critical" || signal.score >= 85;
          const circleRadius = signal.score >= 85 ? "12" : signal.score >= 70 && signal.score < 85 ? "10" : signal.score >= 50 && signal.score < 70 ? "8" : "5";

          return (
            <g key={signal.id}>
              {/* Main signal dot */}
              <motion.circle
                cx={signal.x}
                cy={signal.y}
                r={circleRadius}
                fill={`rgba(${fillColor}, ${opacity})`}
                className="cursor-pointer"
                onMouseEnter={() => setHoveredSignal(signal)}
                onMouseLeave={() => setHoveredSignal(null)}
                animate={{
                  opacity: [0.2, 1, 0.2],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />

              {/* Outer ring */}
              {/* <circle
                cx={signal.x}
                cy={signal.y}
                r={isHighPriority ? "14" : "10"}
                fill="none"
                stroke={`rgba(${fillColor}, ${opacity * 0.6})`}
                strokeWidth="1"
                className="animate-ping"
              /> */}

              {/* Additional rings for high priority signals */}
              {isHighPriority && (
                <>
                  <circle
                    cx={signal.x}
                    cy={signal.y}
                    r="18"
                    fill="none"
                    stroke={`rgba(${fillColor}, 0.4)`}
                    strokeWidth="1"
                    className="animate-ping"
                    style={{
                      animationDuration: "2s",
                      animationDelay: "1s",
                    }}
                  />
                </>
              )}
            </g>
          )
        })}

        {/* Compass directions */}
        <text x="250" y="30" textAnchor="middle" className="fill-gray-600 text-xs font-mono opacity-80">
          N
        </text>
        <text x="470" y="255" textAnchor="middle" className="fill-gray-600 text-xs font-mono opacity-80">
          E
        </text>
        <text x="250" y="485" textAnchor="middle" className="fill-gray-600 text-xs font-mono opacity-80">
          S
        </text>
        <text x="30" y="255" textAnchor="middle" className="fill-gray-600 text-xs font-mono opacity-80">
          W
        </text>
      </svg>

      {/* Enhanced Tooltip */}
      {hoveredSignal && (
        <div
          className="absolute z-50 bg-white border-2 border-gray-300 rounded-lg p-4 pointer-events-none min-w-[300px] max-w-[400px] shadow-xl"
          style={{
            left: (() => {
              const tooltipWidth = 400
              const padding = 20
              const containerWidth = mousePos.containerWidth || 1200

              // Try to position to the right first
              if (mousePos.x + tooltipWidth + padding < containerWidth) {
                return mousePos.x + 15
              }
              // If not enough space on right, position to the left
              else if (mousePos.x - tooltipWidth - padding > 0) {
                return mousePos.x - tooltipWidth - 15
              }
              // Center it if neither side has enough space
              else {
                return Math.max(
                  padding,
                  Math.min(mousePos.x - tooltipWidth / 2, containerWidth - tooltipWidth - padding),
                )
              }
            })(),
            top: (() => {
              const tooltipHeight = 300 // Approximate height
              const padding = 20
              const containerHeight = mousePos.containerHeight || 800

              // Try to position below first
              if (mousePos.y + tooltipHeight + padding < containerHeight) {
                return mousePos.y + 15
              }
              // If not enough space below, position above
              else if (mousePos.y - tooltipHeight - padding > 0) {
                return mousePos.y - tooltipHeight - 15
              }
              // Center it vertically if neither position works
              else {
                return Math.max(
                  padding,
                  Math.min(mousePos.y - tooltipHeight / 2, containerHeight - tooltipHeight - padding),
                )
              }
            })(),
          }}
        >
          <div className="text-xs space-y-3">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 pb-2">
              <span className={`px-2 py-1 rounded text-xs font-bold ${getTagColor(hoveredSignal.score)}`}>
                {hoveredSignal.signal_type}
              </span>
              <span className="text-gray-500 font-mono">Score: {hoveredSignal.score}</span>
            </div>

            {/* Signal Content */}
            <div className="space-y-2">
              <div>
                <span className="text-gray-500 font-semibold">Signal:</span>
                <p className="text-gray-700 mt-1 leading-relaxed">{truncateText(hoveredSignal.raw_signal, 150)}</p>
              </div>

              <div>
                <span className="text-gray-500 font-semibold">Analysis:</span>
                <p className="text-gray-700 mt-1 leading-relaxed">{truncateText(hoveredSignal.enrichment, 200)}</p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-2 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Decision:</span>
                <span className="text-gray-700 font-semibold">{hoveredSignal.decision}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500">Timestamp:</span>
                <span className="text-gray-700 font-mono text-xs">{formatTimestamp(hoveredSignal.timestamp)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Panel */}
      <div className="absolute bottom-[-18px] left-[-40px] text-xs text-gray-600 font-mono space-y-1">
        <div>RANGE: 250km</div>
        <div>SWEEP: {sweepAngle.toFixed(0)}°</div>
        <div>SIGNALS: {signals.length}</div>
        <div className="mt-2 space-y-1">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            <span>Critical: {signals.filter((s) => s.type === "critical").length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
            <span>High: {signals.filter((s) => s.type === "high").length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span>Medium: {signals.filter((s) => s.type === "medium").length}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Low: {signals.filter((s) => s.type === "low").length}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RadarDisplay
